import { useState, useEffect } from 'react';
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NewPage from '../../../layouts/page/NewPage';
import InputText from '../../../components/Forms/Element/InputText';
import AsteriskRequired from '../../../components/Common/AsteriskRequired';
import MenuConfig from '../../../../constants/MenuConfig';
import AnnouncementApiService from '../../../../services/superadmin/AnnouncementApiService';
import { errorAlert } from '../../../components/alert/defaultAlert';
import { successAlert } from '../../../components/alert/defaultAlert';
import FormSection from '../../../components/Common/Form/FormSection';
import './Announcement.css';

const AnnouncementNew = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const formik = useFormik({
        initialValues: {
            title: '',
            body: '',
            linkUrl: '',
            linkLabel: '',
            expiresAt: '',
        },
        validationSchema: Yup.object().shape({
            title: Yup.string().trim().required('Título é obrigatório').max(100, 'Máximo 100 caracteres'),
            body: Yup.string().trim().nullable(),
            linkUrl: Yup.string().url('URL inválida').nullable(),
            linkLabel: Yup.string().max(50, 'Máximo 50 caracteres').nullable(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                let imageUrl = formik.values.imageUrl || null;

                // Se houver nova imagem selecionada, faz upload antes de salvar
                if (imageFile) {
                    const uploadResp = await AnnouncementApiService.uploadImage(imageFile);
                    imageUrl = uploadResp.data;
                }

                const payload = {
                    ...values,
                    imageUrl,
                    expiresAt: values.expiresAt ? values.expiresAt.replace('T', ' ') + ':00' : null,
                    id: isEdit ? parseInt(id) : null,
                };

                if (isEdit) {
                    await AnnouncementApiService.update(id, payload);
                } else {
                    await AnnouncementApiService.create(payload);
                }

                successAlert(t('message.operation_successfully'), () => {
                    navigate(MenuConfig.superadmin.announcement.route);
                });
            } catch (error) {
                errorAlert(error);
            } finally {
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        if (isEdit) {
            AnnouncementApiService.findById(id)
                .then(resp => {
                    const d = resp.data;
                    formik.setValues({
                        title: d.title || '',
                        body: d.body || '',
                        linkUrl: d.linkUrl || '',
                        linkLabel: d.linkLabel || '',
                        expiresAt: d.expiresAt ? d.expiresAt.replace(' ', 'T').slice(0, 16) : '',
                        imageUrl: d.imageUrl || null,
                    });

                    if (d.imageUrl) {
                        AnnouncementApiService.downloadImage(id)
                            .then(imgResp => {
                                const blob = new Blob([imgResp.data]);
                                setImagePreview(URL.createObjectURL(blob));
                            })
                            .catch(() => {});
                    }
                })
                .catch(err => errorAlert(err));
        }
        // eslint-disable-next-line
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    return (
        <NewPage
            motherMenu="Administração"
            childMenu="Comunicados"
            activeMenu={isEdit ? 'Editar' : 'Novo'}
            cancelRoute={MenuConfig.superadmin.announcement.route}
            isSubmitting={formik.isSubmitting}
            handleSubmit={(e) => { e.preventDefault(); formik.handleSubmit(); }}
            child={
                <div className="form-layout form-layout--spaced">
                    <FormSection
                        title="Dados do comunicado"
                        description="Preencha as informações que serão exibidas para os tenants."
                        icon="fa-solid fa-bullhorn"
                    >

                        <Row className="g-3">
                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>Título <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="title"
                                        placeholder="Ex: Nova funcionalidade disponível!"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                        error={formik.touched.title ? formik.errors.title : ''}
                                        maxLength="100"
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>Mensagem</FormLabel>
                                    <textarea
                                        className="form-control"
                                        name="body"
                                        rows={4}
                                        placeholder="Descreva o comunicado..."
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.body}
                                        maxLength={2000}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>URL do Link</FormLabel>
                                    <InputText
                                        name="linkUrl"
                                        placeholder="https://..."
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.linkUrl}
                                        error={formik.touched.linkUrl ? formik.errors.linkUrl : ''}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Rótulo do Botão</FormLabel>
                                    <InputText
                                        name="linkLabel"
                                        placeholder="Ex: Saiba mais"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.linkLabel}
                                        error={formik.touched.linkLabel ? formik.errors.linkLabel : ''}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Expira em</FormLabel>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        name="expiresAt"
                                        onChange={formik.handleChange}
                                        value={formik.values.expiresAt}
                                    />
                                    <small className="text-muted">Deixe em branco para não expirar.</small>
                                </FormGroup>
                            </Col>
                        </Row>
                    </FormSection>

                    <FormSection
                        title="Imagem"
                        description="Imagem de destaque exibida no topo do comunicado (opcional)."
                        icon="fa-solid fa-image"
                    >

                        <Row className="g-3">
                            <Col md={12}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control"
                                    onChange={handleImageChange}
                                />
                            </Col>
                            {imagePreview && (
                                <Col md={12}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="announcement__image-preview"
                                    />
                                </Col>
                            )}
                        </Row>
                    </FormSection>
                </div>
            }
        />
    );
};

export default AnnouncementNew;
